import { memo, useMemo, useState, Fragment } from 'react';
import { Box, Button, Flex, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthContext } from 'context/authContext';
import { STATUS } from 'projectConstants';
import { ExportDataRequestBody } from 'models/requestBody/createExportData.interface';
import {
  getBlockDetails,
  getDimension,
  getIndicatorTableStatus,
  getModelDetails,
  overallIndicatorFilter,
} from 'redux/PlanPageSlice';
import {
  getBaseScenario,
  getCompareTableFilter,
  getExpandKey,
  getTableData,
  getTableHeaderData,
  getVarianceFilterTable,
  setCompareTableFilter,
  setExpandKey,
  toggleVarianceFilterTable,
} from 'redux/ScenarioSlice';
import apiService from 'services';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import saveFile, { buildBlockExportFileName } from 'utils/file';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_KPI_SELECTED_BG,
  PLAN_KPI_SELECTED_BORDER,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
} from '../planPageRevampUi';

import PlanCompareDataTableRow from './PlanCompareDataTableRow';
import PlanDataTableBodySkeleton from './PlanDataTableBodySkeleton';
import PlanDataTableToolbar from './PlanDataTableToolbar';

import type { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanCompareDataTable = ({ onToggleAuthModal, pageScrollMode = false }: Properties) => {
  const { blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const tableData = useSelector(getTableData);
  const tableHeaderData = useSelector(getTableHeaderData);
  const tableFilter = useSelector(getCompareTableFilter);
  const expandKey = useSelector(getExpandKey);
  const varianceFilter = useSelector(getVarianceFilterTable);
  const modelDetail = useSelector(getModelDetails);
  const blockDetails = useSelector(getBlockDetails);
  const dimension = useSelector(getDimension);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const baseScenario = useSelector(getBaseScenario);
  const themeColors = useSelector(getPlanPageThemeColors);
  const compareTableStatus = useSelector(getIndicatorTableStatus);
  const [isExporting, setIsExporting] = useState(false);

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const visibleHeaders = (tableHeaderData || []).filter((item: any) => item?.isShow);

  const onExpandHeader = (header: any) => {
    if (!header?.isShowExpandable || timeAggregation === 'Y') return;
    if (expandKey !== header.name && (tableFilter === 'Y' || tableFilter === 'Q')) {
      dispatch(setExpandKey(header.name));
    } else {
      dispatch(setExpandKey(''));
    }
  };

  const exportRequestBody: ExportDataRequestBody = useMemo(
    () => ({
      orient: 'table',
      query_params: {
        rows: [{ name: 'Indicators', filter: indicatorFilter }, { name: dimension?.name || null }],
        columns: [{ name: 'Time', display_levels: [tableFilter] }],
        filters: [],
      },
      scenario_id: baseScenario?.id,
    }),
    [baseScenario?.id, dimension?.name, indicatorFilter, tableFilter],
  );

  const { refetch: fetchExportData } = useQuery(
    ['compare-export', blockId, baseScenario?.id, tableFilter],
    () =>
      apiService.create_block_export({
        block_id: Number(blockId),
        request_body: exportRequestBody,
      }),
    {
      enabled: false,
      onError: (response: any) => {
        if (response.response?.data?.message) {
          navigate('/error');
        }
      },
    },
  );

  const onDownload = async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const { data: exportData } = await fetchExportData();
      if (exportData) {
        const fileName = buildBlockExportFileName(blockDetails?.name);
        const blob = [exportData.data];
        const contentType =
          exportData.headers?.['content-type'] ??
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        saveFile(fileName, blob, contentType);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const onTimeFilterChange = (filterKey: PlanTimeFilterValue) => {
    dispatch(setCompareTableFilter(filterKey));
  };

  const isTableLoading =
    compareTableStatus === STATUS.LOADING ||
    (compareTableStatus === STATUS.IDLE && !tableHeaderData);
  const hasCachedTable = Boolean((tableData as unknown[])?.length);
  const showTableSkeleton = isTableLoading && !hasCachedTable;
  const isTableEmpty =
    compareTableStatus === STATUS.SUCCESS && (!tableData || (tableData as unknown[]).length === 0);

  const varianceToggle = (
    <Flex alignItems='center' px={3} h='100%'>
      <Button
        size='sm'
        h='28px'
        minH='28px'
        w='28px'
        minW='28px'
        p={0}
        {...PLAN_TEXT.labelSmMedium}
        borderRadius='8px'
        border='1px solid'
        borderColor={varianceFilter ? themeColors.primary : themeColors.border}
        bg={themeColors.surface}
        color={themeColors.primary}
        _hover={{ borderColor: themeColors.primary }}
        onClick={() => dispatch(toggleVarianceFilterTable())}
      >
        %
      </Button>
    </Flex>
  );

  if (showTableSkeleton) {
    return (
      <Flex
        direction='column'
        flex={pageScrollMode ? '1 1 auto' : '1 1 0'}
        minH={pageScrollMode ? undefined : 0}
        minW={0}
        bg={themeColors.surface}
      >
        <PlanDataTableToolbar
          timeAggregation={timeAggregation}
          tableFilter={tableFilter as PlanTimeFilterValue}
          onTimeFilterChange={onTimeFilterChange}
          onDownload={onDownload}
          isExporting={isExporting}
          leadingActions={varianceToggle}
        />
        <Box
          flex={pageScrollMode ? undefined : '1'}
          minH={pageScrollMode ? undefined : 0}
          minW={0}
          overflowX='auto'
          overflowY={pageScrollMode ? 'visible' : 'auto'}
          className={pageScrollMode ? undefined : 'scroll'}
        >
          <PlanDataTableBodySkeleton columnCount={5} />
        </Box>
      </Flex>
    );
  }

  if (!tableHeaderData || isTableEmpty) {
    return (
      <Flex
        h={pageScrollMode ? undefined : '100%'}
        minH='200px'
        alignItems='center'
        justifyContent='center'
        bg={themeColors.surface}
      >
        <Text {...PLAN_TEXT.body} color={themeColors.textSecondary} textAlign='center'>
          No compare table data available for the current filters.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction='column'
      flex={pageScrollMode ? '1 1 auto' : '1 1 0'}
      minH={pageScrollMode ? undefined : 0}
      minW={0}
      bg={themeColors.surface}
    >
      <PlanDataTableToolbar
        timeAggregation={timeAggregation}
        tableFilter={tableFilter as PlanTimeFilterValue}
        onTimeFilterChange={onTimeFilterChange}
        onDownload={onDownload}
        isExporting={isExporting}
        leadingActions={varianceToggle}
      />

      <Box
        flex={pageScrollMode ? undefined : '1'}
        minH={pageScrollMode ? undefined : 0}
        minW={0}
        overflowX='auto'
        overflowY={pageScrollMode ? 'visible' : 'auto'}
        className={pageScrollMode ? undefined : 'scroll'}
        position='relative'
      >
        <Table size='sm' variant='unstyled'>
          <Thead position='sticky' top={0} zIndex={2}>
            <Tr>
              <Th
                position='sticky'
                left={0}
                zIndex={3}
                bg={PLAN_KPI_SELECTED_BG}
                py={3}
                px={3}
                minW={PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH}
                borderRight='1px solid'
                borderColor={PLAN_KPI_SELECTED_BORDER}
                {...PLAN_TEXT.tableComfortaa}
                color={themeColors.textDark}
                textTransform='none'
                letterSpacing='normal'
              >
                Metric
              </Th>
              {visibleHeaders.map((item: any, index: number) => {
                const isExpanded = expandKey === item?.name;
                const isExpandable = Boolean(item?.isShowExpandable) && timeAggregation !== 'Y';
                return (
                  <Fragment key={`${item?.name ?? ''}-${index}`}>
                    <Th
                      key={`${item?.name ?? ''}-value-${index}`}
                      py={3}
                      px={5}
                      bg={PLAN_KPI_SELECTED_BG}
                      borderBottom='1px solid'
                      borderRight='1px solid'
                      borderColor={PLAN_KPI_SELECTED_BORDER}
                      {...PLAN_TEXT.tableComfortaa}
                      color={themeColors.textDark}
                      textTransform='none'
                      letterSpacing='normal'
                      textAlign='end'
                      whiteSpace='nowrap'
                      cursor={isExpandable ? 'pointer' : 'default'}
                      onClick={() => onExpandHeader(item)}
                    >
                      <Flex alignItems='center' justifyContent='flex-end' gap={1}>
                        {isExpandable &&
                          (isExpanded ? (
                            <ChevronRightIcon boxSize={3} color={themeColors.primary} />
                          ) : (
                            <ChevronLeftIcon boxSize={3} color={themeColors.primary} />
                          ))}
                        {item?.name}
                      </Flex>
                    </Th>
                    <Th
                      key={`${item?.name ?? ''}-diff-${index}`}
                      py={3}
                      px={3}
                      bg={PLAN_KPI_SELECTED_BG}
                      borderBottom='1px solid'
                      borderRight='1px solid'
                      borderColor={PLAN_KPI_SELECTED_BORDER}
                      {...PLAN_TEXT.labelSmMedium}
                      color={themeColors.textDark}
                      textTransform='none'
                      textAlign='end'
                    >
                      Diff
                    </Th>
                  </Fragment>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {((tableData as any[]) || []).map((row: any) => (
              <PlanCompareDataTableRow key={row.id ?? row.name} row={row} expandKey={expandKey} />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Flex>
  );
};

export default memo(PlanCompareDataTable);
